/**
 * Configurable components inventory.
 *
 * Grouped list of every component registered via
 * `registerComponent()` across all known modules. Rows link to the
 * existing `/admin/components/:componentId/config` detail page.
 *
 * The registry is populated lazily as modules import their `register.ts`,
 * so on a fresh admin session it is empty. This page mounts the loaders
 * declared in `configurableModuleLoaders.ts` (dynamic imports, so the
 * admin chunk is not bloated) before reading the registry.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings2 } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { Badge, Icon, Text } from '@components/base/display';
import { Alert, Spinner } from '@components/base/feedback';
import { Card, HStack, VStack } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';

import {
  getAllComponents,
  type ComponentRegistration,
} from '@components/shared/configurable';

import { getPropNamesFromRegistration } from './propIntrospection';
import { CONFIGURABLE_MODULE_LOADERS } from './configurableModuleLoaders';

type LoadState = 'loading' | 'ready' | 'error';

interface ModuleGroup {
  module: string;
  registrations: ComponentRegistration<unknown>[];
}

function groupByModule(
  registrations: ComponentRegistration<unknown>[],
): ModuleGroup[] {
  const byModule = new Map<string, ComponentRegistration<unknown>[]>();
  for (const reg of registrations) {
    const existing = byModule.get(reg.module);
    if (existing) {
      existing.push(reg);
    } else {
      byModule.set(reg.module, [reg]);
    }
  }
  return Array.from(byModule.entries())
    .map(([module, regs]) => ({
      module,
      registrations: [...regs].sort((a, b) => a.id.localeCompare(b.id)),
    }))
    .sort((a, b) => a.module.localeCompare(b.module));
}

export function ComponentsListPage() {
  const navigate = useNavigate();
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loaders = Object.values(CONFIGURABLE_MODULE_LOADERS);

    Promise.all(loaders.map((load) => load()))
      .then(() => {
        if (!cancelled) setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load module registrations.');
        setLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(
    () => (loadState === 'ready' ? groupByModule(getAllComponents()) : []),
    [loadState],
  );

  const totalComponents = useMemo(
    () => groups.reduce((acc, g) => acc + g.registrations.length, 0),
    [groups],
  );

  return (
    <VStack gap={6}>
      <Breadcrumb>
        <Breadcrumb.Item href="/admin">Admin</Breadcrumb.Item>
        <Breadcrumb.Item isCurrent>Components</Breadcrumb.Item>
      </Breadcrumb>

      <HStack justify="between" align="start">
        <VStack gap={2}>
          <HStack gap={3} align="center">
            <Icon icon={Settings2} size="md" color="primary" />
            <Text as="h1" size="2xl" weight="bold">
              Configurable Components
            </Text>
            {loadState === 'ready' && (
              <Badge variant="info" size="sm">
                {totalComponents} registered
              </Badge>
            )}
          </HStack>
          <Text size="sm" color="muted">
            Every component declared via <code>registerComponent()</code>, grouped by module.
            Click a row to edit prop labels and rules.
          </Text>
        </VStack>
      </HStack>

      {loadState === 'loading' && (
        <VStack gap={6} align="center" className="py-12">
          <Spinner size="lg" />
          <Text color="muted">Loading module registrations...</Text>
        </VStack>
      )}

      {loadState === 'error' && (
        <Alert variant="error" title="Failed to load module registrations">
          {loadError ?? 'An unexpected error occurred.'}
        </Alert>
      )}

      {loadState === 'ready' && totalComponents === 0 && (
        <Alert variant="info" title="No configurable components registered">
          No module has called <code>registerComponent()</code> yet. Add the module to
          <code> src/modules/admin/pages/component-config/configurableModuleLoaders.ts</code>
          so its registrations load on this page.
        </Alert>
      )}

      {loadState === 'ready' &&
        groups.map((group) => (
          <Card key={group.module}>
            <Card.Header
              title={group.module}
              subtitle={`${String(group.registrations.length)} component${
                group.registrations.length === 1 ? '' : 's'
              }`}
            />
            <Card.Content>
              <Table>
                <Table.Head>
                  <Table.Row>
                    <Table.HeaderCell>ID</Table.HeaderCell>
                    <Table.HeaderCell>Display name</Table.HeaderCell>
                    <Table.HeaderCell align="center">Props</Table.HeaderCell>
                    <Table.HeaderCell align="center">Labelled</Table.HeaderCell>
                    <Table.HeaderCell align="right">Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {group.registrations.map((reg) => {
                    const propNames = getPropNamesFromRegistration(reg);
                    const labelledCount = Object.keys(reg.defaultPropLabels).length;
                    const configHref = `/admin/components/${reg.id}/config`;
                    return (
                      <Table.Row
                        key={reg.id}
                        onClick={() => navigate(configHref)}
                      >
                        <Table.Cell>
                          <Text size="sm" weight="medium" className="font-mono">
                            {reg.id}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text>{reg.displayName}</Text>
                        </Table.Cell>
                        <Table.Cell align="center">
                          <Text size="sm" color="muted">
                            {propNames.length}
                          </Text>
                        </Table.Cell>
                        <Table.Cell align="center">
                          <Text size="sm" color="muted">
                            {labelledCount}
                          </Text>
                        </Table.Cell>
                        <Table.Cell align="right">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Configure ${reg.displayName}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              void navigate(configHref);
                            }}
                          >
                            Configure
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Card.Content>
          </Card>
        ))}
    </VStack>
  );
}
